import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SavedAddress } from "../../types/ordering";

// 2.6 — Address book. Backend ka address collection Phase 3 me aayega
// (ticket 3.1). Tab tak addresses sirf isi browser me rehte hain, cart ki
// tarah localStorage me. Checkout PlaceOrderRequest.address me inline bhejega.

const STORAGE_KEY = "ci_addresses_v1";
const MAX_ADDRESSES = 20;

export interface AddressState {
  list: SavedAddress[];
  defaultId: string | null;
}

const emptyState: AddressState = { list: [], defaultId: null };

export const makeAddressId = (): string => {
  try {
    const c: any = typeof crypto !== "undefined" ? crypto : null;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
  } catch (e) {
    // ignore
  }
  return `addr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

const str = (v: any): string => (typeof v === "string" ? v.trim() : "");

/** Adhoora ya galat address kabhi store me nahi jayega */
export const cleanAddress = (a: any): SavedAddress | null => {
  if (!a) return null;
  const address1 = str(a.address1);
  const city = str(a.city);
  const pinCode = str(a.pinCode);
  if (!address1 || !city || !/^[0-9]{6}$/.test(pinCode)) return null;
  return {
    id: str(a.id) || makeAddressId(),
    label: str(a.label) || undefined,
    contactPerson: str(a.contactPerson) || undefined,
    phone: str(a.phone) || undefined,
    address1,
    address2: str(a.address2) || undefined,
    city,
    state: str(a.state) || undefined,
    pinCode,
  };
};

export const loadAddresses = (): AddressState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...emptyState };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.list)) return { ...emptyState };
    const list = parsed.list
      .map(cleanAddress)
      .filter((a: SavedAddress | null): a is SavedAddress => a !== null)
      .slice(0, MAX_ADDRESSES);
    const defaultId =
      typeof parsed.defaultId === "string" &&
      list.some((a: SavedAddress) => a.id === parsed.defaultId)
        ? parsed.defaultId
        : list.length
        ? list[0].id
        : null;
    return { list, defaultId };
  } catch (e) {
    return { ...emptyState };
  }
};

export const saveAddresses = (state: AddressState): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    // private window / storage full — chup-chaap chhod do
  }
};

export type AddressDraft = Omit<SavedAddress, "id"> & { id?: string };

const addressSlice = createSlice({
  name: "address",
  initialState: loadAddresses(),
  reducers: {
    /** naya address, ya id mile to usi ko update */
    upsertAddress: (state, action: PayloadAction<AddressDraft>) => {
      const clean = cleanAddress(action.payload);
      if (!clean) return;
      const idx = state.list.findIndex((a) => a.id === clean.id);
      if (idx >= 0) {
        state.list[idx] = clean;
      } else {
        if (state.list.length >= MAX_ADDRESSES) return;
        state.list.push(clean);
      }
      if (!state.defaultId) state.defaultId = clean.id;
    },
    removeAddress: (state, action: PayloadAction<string>) => {
      state.list = state.list.filter((a) => a.id !== action.payload);
      if (state.defaultId === action.payload) {
        state.defaultId = state.list.length ? state.list[0].id : null;
      }
    },
    setDefaultAddress: (state, action: PayloadAction<string>) => {
      if (state.list.some((a) => a.id === action.payload)) {
        state.defaultId = action.payload;
      }
    },
    clearAddresses: (state) => {
      state.list = [];
      state.defaultId = null;
    },
  },
});

export const {
  upsertAddress,
  removeAddress,
  setDefaultAddress,
  clearAddresses,
} = addressSlice.actions;

export const selectAddresses = (state: {
  address: AddressState;
}): SavedAddress[] => state.address.list;

export const selectDefaultAddressId = (state: {
  address: AddressState;
}): string | null => state.address.defaultId;

export const selectDefaultAddress = (state: {
  address: AddressState;
}): SavedAddress | null =>
  state.address.list.find((a) => a.id === state.address.defaultId) || null;

export default addressSlice.reducer;
